// Filtry klienta — wejście do edycji. Support wybiera użytkownika i widzi jego zapisane
// filtry razem z tym, czego nie widać po nazwie: co filtr faktycznie zawęża i czy w ogóle
// cokolwiek wysyła.

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { App, Button, Card, Form, Select, Space, Table, Tag, Typography } from "antd";
import { FiltersCell } from "../../components/FiltersPreview";
import { ActiveSubscriptionSwitch } from "../../components/ActiveSubscriptionSwitch";
import { adminFetch, errorMessage } from "../../lib/adminApi";
import { useSupabaseUsers } from "../../lib/useSupabaseUsers";
import type { UserFilterOption } from "../../types/filterCopy";
import type { NotificationSubscription } from "../../types/filterAdmin";
import { SEND_INTERVAL_LABELS } from "../../types/filterAdmin";

const { Title, Paragraph, Text } = Typography;

/** Stan powiadomień jako zestaw chipów: częstotliwości właściciela albo wprost „nie wysyła”. */
function NotificationCell({ notification }: { notification: NotificationSubscription | null }) {
  if (!notification || notification.intervals.length === 0) return <Text type="secondary">nie wysyła</Text>;
  return (
    <Space size={[4, 4]} wrap>
      {notification.intervals.map((interval) => (
        <Tag key={interval} color={interval === "instantly" ? "purple" : "green"}>
          {SEND_INTERVAL_LABELS[interval]?.split(" (")[0] ?? interval}
        </Tag>
      ))}
    </Space>
  );
}

export const FiltersList = () => {
  const { notification } = App.useApp();
  const [activeOnly, setActiveOnly] = useState(true);
  const [userId, setUserId] = useState<string | undefined>();
  const { users, loading: usersLoading } = useSupabaseUsers(activeOnly, [userId]);
  const [filters, setFilters] = useState<UserFilterOption[]>([]);
  const [filtersLoading, setFiltersLoading] = useState(false);

  const loadFilters = useCallback(
    async (owner: string) => {
      setFiltersLoading(true);
      try {
        const response = await adminFetch<{ data: UserFilterOption[] }>(
          `supabase/users/${encodeURIComponent(owner)}/user-filters`,
        );
        setFilters(response.data ?? []);
      } catch (error) {
        setFilters([]);
        notification.error({
          message: "Nie udało się pobrać filtrów",
          description: errorMessage(error),
        });
      } finally {
        setFiltersLoading(false);
      }
    },
    [notification],
  );

  useEffect(() => {
    if (userId) {
      loadFilters(userId);
    } else {
      setFilters([]);
    }
  }, [userId, loadFilters]);

  return (
    <Card>
      <Title level={4}>Filtry klienta</Title>
      <Paragraph type="secondary">
        Wybierz użytkownika, żeby zobaczyć i edytować jego zapisane filtry — kryteria oraz
        ustawienia raportowania (e-mail/SMS, częstotliwość). Zmiany są natychmiast widoczne
        dla klienta we froncie.
      </Paragraph>

      <Form layout="vertical" style={{ maxWidth: 480 }}>
        <Form.Item
          label="Użytkownik"
          extra={<ActiveSubscriptionSwitch checked={activeOnly} onChange={setActiveOnly} />}
        >
          <Select
            showSearch
            allowClear
            placeholder="Wybierz użytkownika"
            loading={usersLoading}
            value={userId}
            onChange={setUserId}
            optionFilterProp="label"
            options={users.map((user) => ({ value: user.id, label: user.email || user.id }))}
          />
        </Form.Item>
      </Form>

      <Table
        rowKey="id"
        dataSource={filters}
        loading={filtersLoading}
        pagination={false}
        locale={{ emptyText: userId ? "Ten użytkownik nie ma zapisanych filtrów." : "Wybierz użytkownika." }}
        columns={[
          {
            title: "Nazwa",
            dataIndex: "name",
            width: 220,
            render: (name: string, row: UserFilterOption) => (
              <Space direction="vertical" size={2}>
                <Text strong>{name || row.id}</Text>
                {row.public && <Tag color="gold">publiczny</Tag>}
              </Space>
            ),
          },
          {
            title: "Zawartość",
            dataIndex: "filters_display",
            render: (_: unknown, row: UserFilterOption) => <FiltersCell filters={row.filters_display} />,
          },
          {
            title: "Powiadomienia",
            dataIndex: "notification",
            width: 220,
            render: (_: unknown, row: UserFilterOption) => (
              <NotificationCell notification={row.notification ?? null} />
            ),
          },
          {
            title: "",
            key: "actions",
            width: 100,
            render: (_: unknown, row: UserFilterOption) => (
              <Link to={`/filters/edit/${encodeURIComponent(row.id)}`}>
                <Button type="link">Edytuj</Button>
              </Link>
            ),
          },
        ]}
      />
    </Card>
  );
};
